// StudioComposer — the one composer at the foot of the Studio's action
// panel, under Chat and Needs you alike (pen "Studio · Composer"): what it
// works on (the scope chip: the page open, or the whole course or project,
// with the other one beside it to switch to; or what is picked as the
// assistant's context, with × to let it go), the field "Ask, or say
// what to change", attach and send; Stop while the assistant answers.
// Enter sends, Shift+Enter breaks the line. The text is the open
// conversation's own draft; there is always one, so the field is always
// typeable (#431). Pure: the draft and the target come in; every
// change goes out as an intent.

export { StudioComposer };

import { useLingui } from "@lingui/react/macro";
import type { ReactNode } from "react";
import { Icon } from "../Icon/Icon.js";
import { IconButton } from "../IconButton/IconButton.js";

const StudioComposer = ({
    draft,
    whole,
    targets,
    page,
    onDraftChange,
    onSend,
    onClearTargets,
    onAttach,
    limitHint,
}: {
    readonly draft: string;
    // What "everything" is when nothing is picked ("Whole course").
    readonly whole: string;
    // What the next message is about, by title (none: the whole).
    readonly targets: readonly string[];
    // The page open, which the chip follows (absent: none, or the project's
    // own page): its title, whether the author widened the scope to the
    // whole, and the two switches.
    readonly page?: {
        readonly title: string;
        readonly whole: boolean;
        readonly onScopeToWhole: () => void;
        readonly onScopeToPage: () => void;
    };
    readonly onDraftChange: (text: string) => void;
    readonly onSend: () => void;
    readonly onClearTargets: () => void;
    readonly onAttach: () => void;
    // A Limit hint above the target chip: a ceiling is reached (CL-21, PS-61),
    // so Send is off and the text stays. Absent: none.
    readonly limitHint?: string;
}): ReactNode => {
    const { t } = useLingui();
    const canSend = draft.trim() !== "" && limitHint === undefined;
    // With nothing picked the chip names the page open, or the whole, and
    // offers the other.
    const scoped = targets.length === 0 ? page : undefined;
    const target =
        targets.length > 0
            ? targets.join(", ")
            : scoped === undefined || scoped.whole
              ? whole
              : scoped.title;
    return (
        <form
            className="bilda-studio-composer"
            onSubmit={(e) => {
                e.preventDefault();
                if (canSend) onSend();
            }}
        >
            {limitHint === undefined ? null : (
                <p className="bilda-studio-composer__limit" role="status">
                    <Icon name="info" size="xs" />
                    {limitHint}
                </p>
            )}
            <div className="bilda-studio-composer__box">
                <span className="bilda-studio-composer__scope">
                    <span
                        className="bilda-studio-composer__target"
                        title={target}
                    >
                        <Icon name="crosshair" size="xs" />
                        <span className="bilda-studio-composer__target-text">
                            {target}
                        </span>
                        {targets.length === 0 ? null : (
                            <button
                                type="button"
                                className="bilda-studio-composer__target-clear"
                                aria-label={t`Back to the whole project`}
                                onClick={onClearTargets}
                            >
                                <Icon name="close" size="xs" />
                            </button>
                        )}
                    </span>
                    {scoped === undefined ? null : (
                        <button
                            type="button"
                            className="bilda-studio-composer__target-switch"
                            title={scoped.whole ? scoped.title : whole}
                            onClick={
                                scoped.whole
                                    ? scoped.onScopeToPage
                                    : scoped.onScopeToWhole
                            }
                        >
                            {scoped.whole ? scoped.title : whole}
                        </button>
                    )}
                </span>
                <textarea
                    className="bilda-studio-composer__field"
                    rows={2}
                    aria-label={t`Message to the assistant`}
                    placeholder={t`Ask, or say what to change`}
                    value={draft}
                    onChange={(e) => onDraftChange(e.target.value)}
                    onKeyDown={(e) => {
                        if (
                            e.key !== "Enter" ||
                            e.shiftKey ||
                            e.nativeEvent.isComposing
                        )
                            return;
                        e.preventDefault();
                        if (canSend) onSend();
                    }}
                />
                <span className="bilda-studio-composer__actions">
                    <IconButton
                        label={t`Attach a file`}
                        variant="ghost"
                        size="sm"
                        type="button"
                        onClick={onAttach}
                    >
                        <Icon name="paperclip" />
                    </IconButton>
                    {/* PS-61: always Send, never Stop (Stop is on the
                        running step); while the assistant works a message
                        waits for its next step. */}
                    <IconButton
                        label={t`Send`}
                        variant="primary"
                        size="md"
                        className="bilda-studio-composer__send"
                        type="submit"
                        disabled={!canSend}
                    >
                        <Icon name="arrow-up" />
                    </IconButton>
                </span>
            </div>
        </form>
    );
};
